/**
 * Live credit event stream — hierarchy `GET /v1/agents/:id/credit-stream` (SSE).
 *
 * Confirmed give/take events refresh balances without Socket.IO. Uses fetch +
 * ReadableStream (not EventSource) so the Bearer token can be sent.
 */

import { useEffect, useRef, useState } from 'react'
import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import { getAccessToken } from '@/auth/oidc'
import { getApiBaseUrl, getCallerAgentId, hasLiveApi } from '@/api/client'
import { invalidateAfterCreditTransfer } from '@/api/hooks/credit/invalidateAfterCreditTransfer'
import { parseSseChunk } from '@/lib/sse'

export interface CreditTransferredEvent {
  type: 'credit.transferred'
  targetId: string
  parentId: string | null
  direction: 'give' | 'take'
  amount: string
  transferId: string
  at: string
  replayed: boolean
}

export type CreditStreamStatus = 'idle' | 'connecting' | 'open' | 'closed'

/** Pure SSE frame handler — shared by the hook and unit tests. */
export function handleCreditStreamSseFrame(
  name: string,
  data: string,
  client: QueryClient,
): void {
  if (name !== 'credit.transferred' && name !== 'balance.updated') return
  try {
    const event = JSON.parse(data) as CreditTransferredEvent | { type?: string }
    if (
      event.type === 'credit.transferred' ||
      event.type === 'balance.updated' ||
      ('transferId' in event && event.transferId)
    ) {
      invalidateAfterCreditTransfer(client)
    }
  } catch {
    // ignore malformed frames
  }
}

function streamUrl(agentId: string): string {
  const base = getApiBaseUrl()
  if (!base) throw new Error('VITE_API_BASE_URL is not set')
  return `${base}/v1/agents/${agentId}/credit-stream`
}

/**
 * Subscribe while a Live Keycloak session is active. Demo sessions stay on
 * fixtures and never open the stream.
 */
export function useCreditEventStream(): CreditStreamStatus {
  const client = useQueryClient()
  const [status, setStatus] = useState<CreditStreamStatus>('idle')
  const clientRef = useRef(client)
  clientRef.current = client

  useEffect(() => {
    if (!hasLiveApi()) {
      setStatus('idle')
      return
    }
    const agentId = getCallerAgentId()
    if (!agentId) {
      setStatus('idle')
      return
    }

    const ac = new AbortController()
    let cancelled = false
    let retryTimer: ReturnType<typeof setTimeout> | undefined
    let attempt = 0

    const connect = async () => {
      if (cancelled) return
      setStatus('connecting')
      const token = getAccessToken()?.trim()
      if (!token) {
        setStatus('closed')
        return
      }

      try {
        const response = await fetch(streamUrl(agentId), {
          method: 'GET',
          headers: {
            Accept: 'text/event-stream',
            Authorization: `Bearer ${token}`,
            'X-Caller-Id': agentId,
          },
          signal: ac.signal,
        })
        if (!response.ok || !response.body) {
          throw new Error(`credit-stream ${response.status}`)
        }
        setStatus('open')
        attempt = 0

        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''

        while (!cancelled) {
          const { done, value } = await reader.read()
          if (done) break
          buffer += decoder.decode(value, { stream: true })
          buffer = parseSseChunk(buffer, (name, data) => {
            handleCreditStreamSseFrame(name, data, clientRef.current)
          })
        }
        if (!cancelled) setStatus('closed')
      } catch (err) {
        if (cancelled || (err instanceof DOMException && err.name === 'AbortError')) return
        setStatus('closed')
      }

      if (cancelled) return
      // Reconnect with backoff — stream is the live balance path for give/take.
      attempt += 1
      const delay = Math.min(30_000, 1_000 * 2 ** Math.min(attempt, 4))
      retryTimer = setTimeout(connect, delay)
    }

    void connect()

    return () => {
      cancelled = true
      ac.abort()
      if (retryTimer) clearTimeout(retryTimer)
    }
  }, [])

  return status
}
